import type {EditablePaymentUpdateTerms, PaymentUpdateFile, PaymentUpdateImpact, PaymentUpdateView} from '../dtos/payment-plan-update';
import {wmRequest, wmUrl} from './wm-client';

const path = (uid: string, action = ''): string => wmUrl(`/v1/cases/${uid}/payment-plan-update${action}`);
const pendingReads = new Map<string, Promise<PaymentUpdateView>>();
const read = (uid: string): Promise<PaymentUpdateView> => {
  const existing = pendingReads.get(uid);
  if (existing) return existing;
  const request = wmRequest<PaymentUpdateView>({url: path(uid), method: 'GET'}).finally(() => {
    if (pendingReads.get(uid) === request) pendingReads.delete(uid);
  });
  pendingReads.set(uid, request);
  return request;
};
const write = <T>(config: Parameters<typeof wmRequest>[0]): Promise<T> =>
  wmRequest<T>(config).finally(() => pendingReads.clear());

export const paymentPlanUpdatesApi = {
  get: read,
  saveDraft: (uid: string, expectedVersion: number, terms: EditablePaymentUpdateTerms, formData?: Record<string, unknown>): Promise<PaymentUpdateView> => write({url: path(uid, '/draft'), method: 'PATCH', data: {expectedVersion, terms, ...(formData === undefined ? {} : {formData})}}),
  discardDraft: (uid: string, expectedVersion: number): Promise<PaymentUpdateView> => write({url: path(uid, '/draft'), method: 'DELETE', data: {expectedVersion}}),
  preview: (uid: string, expectedVersion: number): Promise<PaymentUpdateImpact> => write({url: path(uid, '/preview'), method: 'POST', data: {expectedVersion}}),
  pdf: (uid: string, expectedVersion: number): Promise<PaymentUpdateFile> => write({url: path(uid, '/preview/pdf'), method: 'POST', data: {expectedVersion}}),
  share: (uid: string, expectedVersion: number): Promise<PaymentUpdateFile> => write({url: path(uid, '/share'), method: 'POST', data: {expectedVersion}}),
  submit: (uid: string, expectedVersion: number): Promise<PaymentUpdateView> => write({url: path(uid, '/submit'), method: 'POST', data: {expectedVersion}}),
  retryGeneration: (uid: string, expectedRevision: number): Promise<PaymentUpdateView> => write({url: path(uid, '/generation/retry'), method: 'POST', data: {expectedRevision}}),
  attach: (uid: string, expectedVersion: number, file: File): Promise<PaymentUpdateView> => {
    const data = new FormData();
    data.append('expectedVersion', String(expectedVersion));
    data.append('file', file);
    return write({url: path(uid, '/attachments'), method: 'POST', data, formData: true});
  },
  removeAttachment: (uid: string, expectedVersion: number, uploadUid: string): Promise<PaymentUpdateView> => write({url: path(uid, `/attachments/${uploadUid}`), method: 'DELETE', data: {expectedVersion}}),
};
